import common from '../../../common/common';
import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import {
  filter,
  find,
  findIndex,
  forEach,
  isEmpty,
  isNaN,
  isNil,
  replace,
  some,
} from 'lodash';
import translateService from '../../../common/translateService.js';
import {
  addToCart,
  addReassNotification,
  getOptionSelected,
  getKitQtyAvailable,
  getAvailabilityStatus,
  getKitAvailabilityStatus,
  getTabsNumber,
  getPreselectionAndModals,
  getColorSelectedFromQueryString,
  getOptionSelectedFromQueryString,
  findColorFromOption,
  findOption,
  getPrice,
} from '../../../services/product/productService';
import productsAvailabilityStatus from './productsAvailabilityStatus';
import recentlyViewedProductsService from '../../../services/product/recentlyViewedProductsService.js';
import marketingAndAnalyticsService from '../../../services/marketingAndAnalytics/marketingAndAnalyticsService.js';
import ProductTags from './ProductTags.jsx';
import ProductHeader from './ProductHeader.jsx';
import ProductCarousel from './ProductCarousel.jsx';
import ProductPrice from './ProductPrice.jsx';
import ProductDestinationNote from './ProductDestinationNote.jsx';
import ProductInfo from './ProductInfo.jsx';
import ProductOptions from './ProductOptions.jsx';
import ProductAvailabilityNote from './ProductAvailabilityNote.jsx';
import ProductQuantity from './ProductQuantity.jsx';
import ProductSelectionHint from './ProductSelectionHint.jsx';
import ProductTabs from './ProductTabs.jsx';
import ProductNotes from './ProductNotes.jsx';
import ProductSummary from './ProductSummary.jsx';
import RelatedProducts from './RelatedProducts.jsx';
import PopUp from '../../PopUp.jsx';
import './ProductDetails.scss';
import { logError } from '../../../tracing.js';

const ProductDetails = ({ slug, enablenews, details }) => {
  Modal.setAppElement('body');
  const noPhoto = {
    big: common.contentFromCdn({
      path: '~/content/products/nophoto_bg.png',
      keepUrlAsIs: false,
    }),
    browse: common.contentFromCdn({
      path: '~/content/products/nophoto_br.png',
      keepUrlAsIs: false,
    }),
    zoom: common.contentFromCdn({
      path: '~/content/products/nophoto_zm.png',
      keepUrlAsIs: false,
    }),
  };
  const isMobile = window.innerWidth < 1024;
  const reviewsRef = useRef(null);
  const sizeChartRef = useRef(null);
  const flcktyHtmlRefSmall = useRef(null);
  const flcktyHtmlRefLarge = useRef(null);
  const [selection, setSelection] = useState({
    carousel: { images: [], initialIndex: 0 },
    attributes: {},
    qtySelected: 1,
    qtyAvailable: 0,
    availabilityStatus: '',
    option: null,
  });
  const [showSelectionHint, setShowSelectionHint] = useState(false);
  const [tabsNumber, setTabsNumber] = useState(null);
  const [flcktyRefSmall, setRefSmall] = useState();
  const [flcktyRefLarge, setRefLarge] = useState();
  const [modalsState, setModalsState] = useState({});
  const [cartModalState, setCartModalState] = useState(false);
  const [productSummary, setProductSummary] = useState({
    isReassortment: false,
    isSuccess: false,
    url: '',
    cartTotalItems: 0,
    productTitle: '',
    productAttributes: [],
    productImageUrl: '',
    productQuantityAdded: 0,
    productPriceDiscounted: 0,
    productPriceFull: 0,
    message: '',
  });

  useEffect(() => {
    initialize(slug);
    recentlyViewedProductsService.add(window.location.href);
  }, []);

  async function initialize(s) {
    try {
      await marketingAndAnalyticsService.viewItemFbq({
        slug: s,
        guid: details.guid,
        options: details.options,
        currencyCode: details.currencyCode,
        isKit: details.isKit,
      });
    } catch (error) {
      console.error('Error in viewItemFbq:', error);
      logError(
        'Error in viewItemFbq',
        error,
        'ProductDetails.jsx',
        'initialize',
      );
    }

    const tabsNum = getTabsNumber(details, enablenews);
    setTabsNumber(tabsNum);

    const optionPreselectionId = getOptionSelectedFromQueryString();
    const colorAttribute = find(details.attributes, (a) => a.kind === 0);
    const colorAttributeName = isNil(colorAttribute)
      ? null
      : colorAttribute.fieldId;
    let optionPreselected = findOption(details.options, optionPreselectionId);
    let colorPreselection = isNil(optionPreselectionId)
      ? getColorSelectedFromQueryString()
      : findColorFromOption(optionPreselected, colorAttributeName);
    const [attr, modals] = getPreselectionAndModals(
      details.attributes,
      details.isKit,
      optionPreselected,
      colorPreselection,
    );

    const isPreselectionIncomplete = some(attr, (value) => value === null);

    if (isNil(optionPreselected) && !isPreselectionIncomplete) {
      optionPreselected = getOptionSelected(
        details.isKit,
        attr,
        details.options,
      );
    }

    if (
      isNil(colorPreselection) &&
      !isNil(colorAttributeName) &&
      !isNil(attr[colorAttributeName])
    ) {
      colorPreselection = attr[colorAttributeName];
    }

    setModalsState(modals);

    let productImages = _.map(details.images, (image) => {
      image.big = common.contentFromCdn({
        path: image.big,
        keepUrlAsIs: false,
      });
      image.browse = common.contentFromCdn({
        path: image.browse,
        keepUrlAsIs: false,
      });
      image.zoom = common.contentFromCdn({
        path: image.zoom,
        keepUrlAsIs: false,
      });
      return image;
    });
    let filteredImages = isNil(colorPreselection)
      ? productImages
      : filter(productImages, (i) => i.color === colorPreselection);
    let initialIndex = isNil(colorPreselection)
      ? 0
      : findIndex(filteredImages, (i) => i.main);

    if (isEmpty(filteredImages)) {
      const colorAttribute = find(details.attributes, (a) => a.kind === 0);
      const foundValue = find(
        colorAttribute.values,
        (v) => v.value === colorPreselection,
      );

      if (!isNil(foundValue)) {
        const foundImage = find(
          productImages,
          (i) => i.big === foundValue.imageBig,
        );

        filteredImages = isNil(foundImage)
          ? [
              {
                index: 0,
                big: noPhoto.big,
                browse: noPhoto.browse,
                zoom: noPhoto.zoom,
                color: null,
              },
            ]
          : [foundImage];
      } else {
        filteredImages = productImages;
      }

      initialIndex = 0;
    }

    let option = null,
      qtyAv = null,
      avStatus = null;
    // non ho attributi, se non è un kit ho una sola option
    if (details.attributes.length === 0) {
      option = details.isKit ? details.options : details.options[0];
      qtyAv = details.isKit
        ? getKitQtyAvailable(details.options)
        : details.options[0].quantity;
      avStatus = details.isKit
        ? getKitAvailabilityStatus(qtyAv, details.options, 1)
        : getAvailabilityStatus(
            details.options[0].quantity,
            details.options[0],
            1,
          );
    }
    // ho almeno un attributo e ho tutto preselezionato: optionPreselected
    else if (!isPreselectionIncomplete) {
      option = optionPreselected;
      qtyAv = details.isKit
        ? getKitQtyAvailable(optionPreselected)
        : isNil(optionPreselected)
        ? 0
        : optionPreselected.quantity;
      avStatus = details.isKit
        ? getKitAvailabilityStatus(qtyAv, details.options, 1)
        : getAvailabilityStatus(qtyAv, optionPreselected, 1);
    }
    // ho almeno un attributo ma la selezione non è completa: rimane null

    setSelection((prevState) => {
      return {
        ...prevState,
        option: option,
        qtyAvailable: isNil(qtyAv) ? prevState.qtyAvailable : qtyAv,
        availabilityStatus: isNil(avStatus)
          ? prevState.availabilityStatus
          : avStatus,
        attributes: attr,
        carousel: {
          images: filteredImages,
          initialIndex: initialIndex,
        },
      };
    });
  }

  const updateSelection = (e, isColor) => {
    if (showSelectionHint) {
      setShowSelectionHint(false);
    }

    const attr = e.currentTarget.dataset.attr;
    const val =
      e.currentTarget.dataset.value === selection.attributes[attr]
        ? null
        : e.currentTarget.dataset.value;

    let foundImages = [],
      initialIndex = 0;

    if (isColor) {
      foundImages = isNil(val)
        ? details.images
        : filter(details.images, (i) => i.color === val);
      initialIndex = findIndex(foundImages, (i) => i.main);

      if (isEmpty(foundImages)) {
        if (isNil(val)) {
          foundImages = details.images;
        } else {
          let foundImage = find(
            details.images,
            (i) => i.browse === e.currentTarget.dataset.image,
          );
          if (isNil(foundImage)) {
            const colorAttribute = find(
              details.attributes,
              (a) => a.kind === 0,
            );
            const foundColor = find(
              colorAttribute.values,
              (v) => v.value === val,
            );
            foundImage = isNil(foundColor)
              ? {
                  index: 0,
                  big: common.contentFromCdn({
                    path: noPhoto.big,
                    keepUrlAsIs: false,
                  }),
                  browse: common.contentFromCdn({
                    path: noPhoto.browse,
                    keepUrlAsIs: false,
                  }),
                  zoom: common.contentFromCdn({
                    path: noPhoto.zoom,
                    keepUrlAsIs: false,
                  }),
                  color: null,
                }
              : {
                  big: common.contentFromCdn({
                    path: foundColor.imageBig,
                    keepUrlAsIs: false,
                  }),
                  browse: common.contentFromCdn({
                    path: foundColor.imageBrowse,
                    keepUrlAsIs: false,
                  }),
                  zoom: common.contentFromCdn({
                    path: foundColor.imageZoom,
                    keepUrlAsIs: false,
                  }),
                  color: foundColor.value,
                  index: foundColor.index,
                };
          }
          foundImages = [foundImage];
        }
        initialIndex = 0;
      }

      if (!isMobile) {
        if (flcktyRefSmall) {
          flcktyRefSmall.select(initialIndex, false, false);
        }
      }
      if (flcktyRefLarge) {
        flcktyRefLarge.select(initialIndex, false, false);
      }
    }

    // setSelection
    setSelection((prevState) => {
      let newAttr = {};

      forEach(prevState.attributes, (value, key) => {
        newAttr[key] = key === attr ? val : value;
      });

      const isAllSelected = !some(newAttr, (v) => v === null);

      const prdOption = isAllSelected
        ? getOptionSelected(details.isKit, newAttr, details.options)
        : null;

      const qtyAvailable = details.isKit
        ? getKitQtyAvailable(prdOption)
        : prdOption
        ? prdOption.quantity
        : 0;

      const availabilityStatus = details.isKit
        ? getKitAvailabilityStatus(
            qtyAvailable,
            prdOption,
            prevState.qtySelected,
          )
        : getAvailabilityStatus(qtyAvailable, prdOption, prevState.qtySelected);

      return {
        ...prevState,
        carousel: isColor
          ? {
              ...prevState.carousel,
              images: foundImages,
            }
          : prevState.carousel,
        attributes: newAttr,
        option: prdOption,
        qtyAvailable: qtyAvailable,
        availabilityStatus: availabilityStatus,
      };
    });
  };

  const checkQty = (e) => {
    const value = Number(e.currentTarget.value);
    if (value > 0) return;

    setSelection((prevState) => {
      const availabilityStatus = details.isKit
        ? getKitAvailabilityStatus(prevState.qtyAvailable, prevState.option, 1)
        : getAvailabilityStatus(prevState.qtyAvailable, prevState.option, 1);
      return {
        ...prevState,
        availabilityStatus: availabilityStatus,
        qtySelected: 1,
      };
    });
  };

  const updateQty = (e) => {
    const value = Number(e.currentTarget.value);
    const q = value !== 0 ? (value > 999 ? 999 : value) : '';

    setSelection((prevState) => {
      const availabilityStatus = details.isKit
        ? getKitAvailabilityStatus(prevState.qtyAvailable, prevState.option, q)
        : getAvailabilityStatus(prevState.qtyAvailable, prevState.option, q);
      return {
        ...prevState,
        availabilityStatus: availabilityStatus,
        qtySelected: q,
      };
    });
  };

  const decreaseQty = () => {
    setSelection((prevState) => {
      const q = prevState.qtySelected - 1;
      const availabilityStatus = details.isKit
        ? getKitAvailabilityStatus(prevState.qtyAvailable, prevState.option, q)
        : getAvailabilityStatus(prevState.qtyAvailable, prevState.option, q);
      return {
        ...prevState,
        availabilityStatus: availabilityStatus,
        qtySelected: q,
      };
    });
  };

  const increaseQty = () => {
    setSelection((prevState) => {
      const q = prevState.qtySelected + 1;
      const availabilityStatus = details.isKit
        ? getKitAvailabilityStatus(prevState.qtyAvailable, prevState.option, q)
        : getAvailabilityStatus(prevState.qtyAvailable, prevState.option, q);
      return {
        ...prevState,
        availabilityStatus: availabilityStatus,
        qtySelected: q,
      };
    });
  };

  const updateModalState = (attrName) => {
    let status = {};

    forEach(modalsState, (value, key) => {
      status[key] = key === attrName ? !value : value;
    });

    const body = document.querySelector('body');
    const html = document.querySelector('html');

    //la sto chiudendo, forzo la rimozione della classe che impedisce lo scroll
    if (
      !status[attrName] &&
      body.classList.contains('ReactModal__Body--open')
    ) {
      body.classList.remove('ReactModal__Body--open');
      html.classList.remove('ReactModal__Body--open');
    }
    // la sto aprendo: se sono in mobile aggiungo la classe a html, in desktop no
    else if (isMobile) {
      html.classList.add('ReactModal__Body--open');
    }

    setModalsState(status);
  };

  const changeCartModalState = () => {
    setCartModalState((prevState) => {
      const body = document.querySelector('body');
      const html = document.querySelector('html');

      //la sto chiudendo, forzo la rimozione della classe che impedisce lo scroll
      if (prevState && body.classList.contains('ReactModal__Body--open')) {
        body.classList.remove('ReactModal__Body--open');
        html.classList.remove('ReactModal__Body--open');
      }
      // la sto aprendo: se sono in mobile aggiungo la classe a html, in desktop no
      else if (isMobile) {
        html.classList.add('ReactModal__Body--open');
      }

      return !prevState;
    });
  };

  const handleButtonClick = async function () {
    if (
      selection.availabilityStatus ===
        productsAvailabilityStatus.discontinued ||
      selection.availabilityStatus ===
        productsAvailabilityStatus.qtyDiscontinued ||
      selection.availabilityStatus ===
        productsAvailabilityStatus.optionUnavailable
    )
      return;

    if (selection.availabilityStatus === '') {
      setShowSelectionHint(true);
      return;
    }

    let options = [];

    if (details.isKit) {
      forEach(selection.option, (option) => {
        let key, value;
        forEach(option.attributes, (v, k) => {
          key = k;
          value = isNaN(Number(v)) ? null : Number(v);
        });
        options.push({ groupId: key, optionId: value });
      });
    } else {
      options.push({
        groupId: '',
        optionId: selection.option.sourceId,
      });
    }

    const qty = selection.qtySelected;
    let price = getPrice(
      details.isKit,
      selection.option,
      details.price,
      selection.qtySelected,
    );

    if (details.selectiveDistribution) {
      window.location = `${details.redirectBuyUrl}${
        details.isKit ? '' : `?option=${options[0].optionId}`
      }`;
      return;
    }

    const result =
      selection.availabilityStatus === productsAvailabilityStatus.unavailable ||
      selection.availabilityStatus === productsAvailabilityStatus.qtyUnavailable
        ? await addReassNotification(slug, qty, options, details.isKit)
        : await addToCart(slug, qty, options, details.isKit);
    setProductSummary(result);

    if (result.isSuccess && !isNil(result.transactionId)) {
      marketingAndAnalyticsService.addToCart({
        isKit: details.isKit,
        guid: details.guid,
        quantity: qty,
        options: options,
        currency: details.currencyCode,
        price: price,
        disciplines: details.disciplines,
        categories: details.categories,
        brandName: details.brandName,
        transactionId: result.transactionId,
      });
    }

    changeCartModalState();
  };

  if (flcktyRefLarge) {
    flcktyRefLarge.on('fullscreenChange', (isFullscreen) => {
      const imgs = flcktyRefLarge.element.querySelectorAll('img');
      const body = document.querySelector('body');

      if (isFullscreen) {
        body.classList.add('no-scroll');

        forEach(imgs, (img) => {
          img.src = common.contentFromCdn({
            path: replace(img.src, '/bg_', '/zm_'),
            keepUrlAsIs: false,
          });
        });
      } else {
        body.classList.remove('no-scroll');

        forEach(imgs, (img) => {
          img.src = common.contentFromCdn({
            path: replace(img.src, '/zm_', '/bg_'),
            keepUrlAsIs: false,
          });
        });
      }
    });
  }
  const lowerPrice = details.isKit
    ? details.lowerPrices[`${details.originId}_NULL_kit_${details.countryCode}`]
    : _.isNil(selection.option)
    ? details.lowerPrices[
        `${details.originId}_${details.options[0].sourceId}_article_${details.countryCode}`
      ]
    : details.lowerPrices[
        `${details.originId}_${selection.option.sourceId}_article_${details.countryCode}`
      ];

  return (
    <>
      {!isMobile && (
        <ProductTags
          latestArrival={details.latestArrival}
          offer={details.offer}
          tags={details.tags}
          offerdata={JSON.stringify(details.offerData)}
        />
      )}
      <div className='prd-images'>
        {isMobile && (
          <ProductHeader
            brandUrl={details.brandUrl}
            brandName={details.brandName}
            revCount={details.reviewsCount}
            ratingAverage={details.ratingAverage}
            title={details.title}
            isMobile={isMobile}
            isKit={details.isKit}
            option={selection.option}
            reviewsRef={reviewsRef}
          />
        )}
        <div
          className={`prd-image ${
            isMobile &&
            (details.latestArrival || details.offer || !isEmpty(details.tags))
              ? 'pt-42'
              : isMobile
              ? 'pt-24'
              : ''
          }`.trim()}
        >
          {isMobile && (
            <ProductTags
              latestArrival={details.latestArrival}
              offer={details.offer}
              tags={details.tags}
              offerdata={JSON.stringify(details.offerData)}
            />
          )}
          {!isEmpty(selection.carousel.images) && (
            <ProductCarousel
              carousel={selection.carousel.images}
              isMobile={isMobile}
              carouselSize='big'
              flcktyHtmlRef={flcktyHtmlRefLarge}
              setRef={setRefLarge}
              flktyRef={flcktyRefLarge}
              fullscreen={true}
              flcktyOption={{
                autoPlay: false,
                cellAlign: 'center',
                contain: true,
                draggable: true,
                fullscreen: true,
                initialIndex: selection.carousel.initialIndex,
                pageDots: isMobile,
                prevNextButtons: false,
                wrapAround: false,
              }}
            />
          )}
          {!isMobile && !isEmpty(selection.carousel.images) && (
            <ProductCarousel
              carousel={selection.carousel.images}
              isMobile={isMobile}
              carouselSize='browse'
              flcktyHtmlRef={flcktyHtmlRefSmall}
              setRef={setRefSmall}
              flktyRef={flcktyRefSmall}
              fullscreen={false}
              flcktyOption={{
                autoPlay: false,
                asNavFor: '.big',
                cellAlign: 'left',
                contain: true,
                draggable: true,
                initialIndex: selection.carousel.initialIndex,
                groupCells: '100%',
                pageDots: false,
                prevNextButtons: true,
                wrapAround: false,
              }}
            />
          )}
        </div>
        {isMobile && (
          <ProductPrice
            basePrice={details.price}
            option={selection.option}
            hasManyPrices={details.hasManyPrices}
            financingEnabled={details.financingEnabled}
            isKit={details.isKit}
            currencySymbol={details.currencySymbol}
            decimalPlaces={details.currencyDecimalPlaces}
            qtySelected={selection.qtySelected}
            isPayPalAvailable={details.isPayPalAvailable}
            isKlarnaAvailable={details.isKlarnaAvailable}
            tagsProps={details.tags}
            culture={details.klarnaCulture}
            lowerPrice={lowerPrice}
          />
        )}
      </div>
      <div className='prd-info'>
        {!isMobile && (
          <>
            <ProductHeader
              brandUrl={details.brandUrl}
              brandName={details.brandName}
              revCount={details.reviewsCount}
              ratingAverage={details.ratingAverage}
              title={details.title}
              isMobile={isMobile}
              isKit={details.isKit}
              option={selection.option}
              reviewsRef={reviewsRef}
            />
            <ProductPrice
              basePrice={details.price}
              option={selection.option}
              hasManyPrices={details.hasManyPrices}
              financingEnabled={details.financingEnabled}
              isKit={details.isKit}
              currencySymbol={details.currencySymbol}
              decimalPlaces={details.currencyDecimalPlaces}
              qtySelected={selection.qtySelected}
              isPayPalAvailable={details.isPayPalAvailable}
              isKlarnaAvailable={details.isKlarnaAvailable}
              securePaymentsUrl={details.securePaymentsUrl}
              tagsProps={details.tags}
              culture={details.klarnaCulture}
              lowerPrice={lowerPrice}
            />
          </>
        )}
        <div className='prd-options'>
          <ProductDestinationNote destinationNote={details.destinationNote} />
          <ProductInfo
            isKit={details.isKit}
            options={selection.option}
            kitAvailable={details.kitAvailable}
          />
          <ProductOptions
            attributes={details.attributes}
            handleUpdateSelection={updateSelection}
            handleModalState={updateModalState}
            isKit={details.isKit}
            options={details.options}
            selection={selection.attributes}
            sizeChartRef={sizeChartRef}
            modalsState={modalsState}
            isMobile={isMobile}
            qtySelected={selection.qtySelected}
          />
          {selection.availabilityStatus && (
            <ProductAvailabilityNote
              availabilityStatus={selection.availabilityStatus}
              qtyAvailable={selection.qtyAvailable}
              option={selection.option}
              isKit={details.isKit}
            />
          )}
          {showSelectionHint && (
            <ProductSelectionHint
              selectionAttributes={selection.attributes}
              productAttributes={details.attributes}
            />
          )}
          <ProductQuantity
            qtySelected={selection.qtySelected}
            qtyAvailable={selection.qtyAvailable}
            availabilityStatus={selection.availabilityStatus}
            handleIncreaseQty={increaseQty}
            handleDecreaseQty={decreaseQty}
            handleUpdateQty={updateQty}
            handleCTAClick={handleButtonClick}
            checkInput={checkQty}
            userAuthenticated={details.userAuthenticated}
            slug={slug}
            selectiveDistribution={details.selectiveDistribution}
          />
        </div>
        <ProductNotes
          freeShipping={details.freeShipping}
          countryHasFreeShipping={details.countryHasFreeShipping}
          freeShippingThreshold={details.freeShippingThreshold}
          currencyDecimalPlaces={details.currencyDecimalPlaces}
          currencySymbol={details.currencySymbol}
          shippingInfoUrl={details.shippingInfoUrl}
          securePaymentsUrl={details.securePaymentsUrl}
        />
      </div>
      <ProductTabs
        isMobile={isMobile}
        slug={slug}
        reviewsRef={reviewsRef}
        sizeChartRef={sizeChartRef}
        description={details.description}
        descriptionAutomated={details.descriptionAutomated}
        descriptionOriginalUrl={details.descriptionOriginalUrl}
        techSpecs={details.techSpecs}
        brandsGuides={details.brandsGuides}
        tabsNumber={tabsNumber}
        userAuthenticated={details.userAuthenticated}
        enableNews={false}
        revNumber={details.reviewsCount}
        revAverage={details.ratingAverage}
        qandaNumber={details.questionsAndAnswersCount}
      />
      {!isEmpty(details.productsRelations) && (
        <div className='prd-relations'>
          <header>
            <h1>{translateService.get(`productDetail.productRelations`)}</h1>
          </header>
          <div className='prd-relations-container'>
            <RelatedProducts
              alreadyHaveRelations={true}
              relations={details.productsRelations}
              slug={details.slug}
            />
          </div>
        </div>
      )}
      <Modal
        isOpen={cartModalState}
        onRequestClose={changeCartModalState}
        contentLabel={translateService.get(
          `productDetail.cart.label.${selection.availabilityStatus}`,
        )}
        closeTimeoutMS={500}
        className={{
          base: 'prd-modal-cart',
          afterOpen: 'prd-modal-cart-afterOpen',
          beforeClose: 'prd-modal-cart-beforeClose',
        }}
        overlayClassName={{
          base: 'prd-modal-cart-overlay',
          afterOpen: 'prd-modal-cart-overlay-afterOpen',
          beforeClose: 'prd-modal-cart-overlay-beforeClose',
        }}
        shouldFocusAfterRender={false}
      >
        <ProductSummary
          data={productSummary}
          handleModalStateChange={changeCartModalState}
          currencySymbol={details.currencySymbol}
        />
      </Modal>
      <PopUp />
    </>
  );
};

export default ProductDetails;
